import {
    createElement,
    type ComponentPropsWithoutRef,
    PropsWithChildren,
    ReactElement,
} from 'react'

import { getColor } from 'components/core/helpers'
import { colors } from 'styles/colors'
import { cssStringToReactStyle } from 'styles/cssStringToReactStyle'
import { theme } from 'styles/theme'

import { TextElementType, TextProps } from './types'

/**
 * A utility component to render text.
 * Pass in a name from our Typography into the variant prop to render with preset styles. Use the `element` prop to determine what element you would like it to render as.
 * Read more in our docs [here](https://www.notion.so/junglescout/Text-Component-Typography-794ff90de29c4603bd4a54cbe1f48a2b?pvs=4).
 *
 * ```jsx
 * <Text variant="headingSm" element="h1" align="center" color="primary600">
 *  Hello World
 * </Text>
 * ```
 *
 */
const Text = <C extends TextElementType = 'span'>({
    element = 'span' as C,
    margin = '0',
    padding = '0',
    align = 'left',
    children,
    variant = 'bodyMain',
    underline = false,
    color = 'textPrimary',
    fontWeight,
    uppercase = false,
    ...domProps
}: PropsWithChildren<TextProps<C>>): ReactElement => {
    const { style: incomingStyle, ...restDom } = domProps

    const variantStyle = cssStringToReactStyle(
        theme.textStyles[variant ?? 'bodyMain']
    )

    const resolvedColor = getColor(color, colors)

    const style = {
        ...variantStyle,
        margin,
        padding,
        textAlign: align,
        textDecoration: underline ? 'underline' : 'none',
        ...(fontWeight !== undefined ? { fontWeight } : {}),
        color: resolvedColor,
        ...(uppercase ? { textTransform: 'uppercase' as const } : {}),
        ...incomingStyle,
    }

    return createElement(
        element,
        { style, ...restDom } as ComponentPropsWithoutRef<C>,
        children
    )
}
Text.displayName = 'Text'

export default Text
